import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { catchError, Observable } from 'rxjs';

import { BasedataService } from '../shared/basedata.service';

import { Contact, Contacts } from './contact';
import { ConfigService } from '@docma-mm-core/config/config.service';

const savedProjectContactsKey = 'mm-savedProjectContacts';

@Injectable()
export class ContactService extends BasedataService {

  private projectUrl = '/projects/';
  private contactUrl = '/contacts';
  private contactConfigurationUrl = '/configuration/contacts';
  constructor(private http: HttpClient, configService: ConfigService) {
    super(configService);
  }

  private getContactUrl() {
    return super.getFullUrl(this.contactUrl);
  }

  private getProjectContactUrl(projectId: number) {
    return super.getFullUrl(this.projectUrl + projectId + this.contactUrl);
  }

  private getProjectContactConfigurationUrl(projectId: number) {
    return super.getFullUrl(this.projectUrl + projectId + this.contactConfigurationUrl);
  }

  // get contacts
  getContacts(top: number = 10000, skip: number = 0): Observable<Contacts> {
    const endpoint = this.getContactUrl();
    let httpParams = new HttpParams();
    if (top) { httpParams = httpParams.set('$top', top.toString()); }
    if (skip) { httpParams = httpParams.set('$skip', skip.toString()); }
    return this.http
      .get<Contacts>(endpoint, {headers: this.getHeaders(), params: httpParams})
      .pipe(catchError(e => this.handleError(e)));
  }

  // get contact, ignore active/deleted
  getContactsIgnoreActiveDeleted(top: number = 10000, skip: number = 0): Observable<Contacts> {
    const endpoint = this.getContactUrl();
    let httpParams = new HttpParams();
    if (top) { httpParams = httpParams.set('$top', top.toString()); }
    if (skip) { httpParams = httpParams.set('$skip', skip.toString()); }
    httpParams = httpParams.set('active', 'null');
    httpParams = httpParams.set('deleted', 'null');
    return this.http
      .get<Contacts>(endpoint, {headers: this.getHeaders(), params: httpParams})
      .pipe(catchError(e => this.handleError(e)));
  }

  // search contacts
  searchContacts(top: number = 10000,
                 skip: number = 0,
                 id: number = null,
                 uid: string = null,
                 firstName: string = null,
                 lastName: string = null,
                 department: string = null,
                 email: string = null,
                 fax: string = null,
                 mobile: string = null,
                 phone: string = null,
                 title: string = null,
                 salutation: string = null,
                 cid: number = null,
                 cuid: string = null,
                 pid: number = null,
                 puid: string = null): Observable<Contacts> {
    const endpoint = this.getContactUrl() + '/search';
    let httpParams = new HttpParams();
    if (top) { httpParams = httpParams.set('$top', top.toString()); }
    if (skip) { httpParams = httpParams.set('$skip', skip.toString()); }
    if (id) { httpParams = httpParams.set('id', id.toString()); }
    if (uid) { httpParams = httpParams.set('uid', uid); }
    if (firstName) { httpParams = httpParams.set('firstName', firstName); }
    if (lastName) { httpParams = httpParams.set('lastName', lastName); }
    if (department) { httpParams = httpParams.set('department', department); }
    if (email) { httpParams = httpParams.set('email', email); }
    if (fax) { httpParams = httpParams.set('fax', fax); }
    if (mobile) { httpParams = httpParams.set('mobile', mobile); }
    if (phone) { httpParams = httpParams.set('phone', phone); }
    if (title) { httpParams = httpParams.set('title', title); }
    if (salutation) { httpParams = httpParams.set('salutation', salutation); }
    if (cid) { httpParams = httpParams.set('cid', cid.toString()); }
    if (cuid) { httpParams = httpParams.set('cuid', cuid); }
    if (pid) { httpParams = httpParams.set('pid', pid.toString()); }
    if (puid) { httpParams = httpParams.set('puid', puid); }
    return this.http
      .get<Contacts>(endpoint, {headers: this.getHeaders(), params: httpParams})
      .pipe(catchError(e => this.handleError(e)));
  }

  // get contact by id
  getContactById(contactId: number): Observable<Contact> {
    const endpoint = this.getContactUrl() + '/' + contactId;
    return this.http
      .get<Contact>(endpoint, {headers: this.getHeaders()})
      .pipe(catchError(e => this.handleError(e)));
  }

  // get contact by uid
  getContactByUid(contactUid: string): Observable<Contact> {
    const endpoint = this.getContactUrl();
    let httpParams = new HttpParams();
    httpParams = httpParams.set('uid', contactUid);
    return this.http
      .get<Contact>(endpoint, {headers: this.getHeaders(), params: httpParams})
      .pipe(catchError(e => this.handleError(e)));
  }

  // create new contact
  createContact(contact: Contact): Observable<Contact> {
    const endpoint = this.getContactUrl();
    return this.http
      .post<Contact>(endpoint, JSON.stringify(contact), {headers: this.getHeaders()})
      .pipe(catchError(e => this.handleError(e)));
  }

  // update existing contact
  updateContact(contactId: number, contact: Contact): Observable<Contact> {
    const endpoint = this.getContactUrl() + '/' + contactId;
    return this.http
      .put<Contact>(endpoint, JSON.stringify(contact), {headers: this.getHeaders()})
      .pipe(catchError(e => this.handleError(e)));
  }

  // get contacts by project
  getProjectContacts(projectId: number, top: number = 10000, skip: number = 0): Observable<Contacts> {
    const endpoint = this.getProjectContactUrl(projectId);
    let httpParams = new HttpParams();
    if (top) { httpParams = httpParams.set('$top', top.toString()); }
    if (skip) { httpParams = httpParams.set('$skip', skip.toString()); }
    httpParams = httpParams.set('$details', '10');
    return this.http
      .get<Contacts>(endpoint, {headers: this.getHeaders(), params: httpParams})
      .pipe(catchError(e => this.handleError(e)));
  }

  // get contact by id and by project
  getProjectContactById(projectId: number, contactId: number): Observable<Contact> {
    const endpoint = this.getProjectContactUrl(projectId) + '/' + contactId;
    return this.http
      .get<Contact>(endpoint, {headers: this.getHeaders()})
      .pipe(catchError(e => this.handleError(e)));
  }

  // get contact by uid and by project
  getProjectContactByUid(projectId: number, contactUid: string): Observable<Contact> {
    const endpoint = this.getProjectContactUrl(projectId);
    let httpParams = new HttpParams();
    httpParams = httpParams.set('uid', contactUid);
    return this.http
      .get<Contact>(endpoint, {headers: this.getHeaders(), params: httpParams})
      .pipe(catchError(e => this.handleError(e)));
  }

  // create new contact to project
  createProjectContact(projectId: number, contact: Contact): Observable<Contact> {
    const endpoint = this.getProjectContactUrl(projectId);
    return this.http
      .post<Contact>(endpoint, JSON.stringify(contact), {headers: this.getHeaders()})
      .pipe(catchError(e => this.handleError(e)));
  }

  activateContactInProject(projectId: number, contactId: number): Observable<Contact> {
    const endpoint = this.getProjectContactConfigurationUrl(projectId) + '/' + contactId + '/activate';
    return this.http
      .put<Contact>(endpoint, null, {headers: this.getHeaders()})
      .pipe(catchError(e => this.handleError(e)));
  }

  deactivateContactInProject(projectId: number, contactId: number): Observable<Contact> {
    const endpoint = this.getProjectContactUrl(projectId) + '/' + contactId;
    return this.http
      .delete(endpoint, {headers: this.getHeaders()})
      .pipe(catchError(e => this.handleError(e)));
  }

  deleteContacts(projectId: number, contactIds: number | number[]): Observable<Partial<{ Deleted: number[] }>> {
    const endpoint = super.getFullUrl(this.projectUrl + projectId + '/configuration/contacts');
    const ids = Array.isArray(contactIds) ? contactIds : [contactIds];

    return this.http
      .delete(endpoint, {
        headers: this.getHeaders(),
        body: ids
      })
      .pipe(catchError(e => this.handleError(e)));
  }
}
